<template>
  <e-sidebar
    id="sidebar-estimate"
    ref="sidebar"
    :title="estimate ? $t('Confirmar solicitação de entrega') : $t('Cotação de entrega')"
    :show="showSidebar"
    :fetching="fetching"
    :saving="saving"
    :confirm-button-text="$t('Solicitar entrega')"
    :hide-save-button="!estimate || fetching"
    width="40%"
    @hidden="hide"
    @save="localCreateDeliveryOrder"
  >
    <template #content>
      <b-row>
        <b-col>
          <p class="h6 mb-1">
            {{ $t('Parceiro de entrega') }}
          </p>

          <div
            v-if="deliverySupplierOptions.length"
            class="d-flex flex-wrap"
            style="gap: 8px"
          >
            <e-button
              v-for="option in deliverySupplierOptions"
              :id="`delivery-estimate-sidebar_supplier-${option.value}`"
              :key="option.value"
              :text="option.label"
              icon="cash"
              :variant="option.value === selectedDeliverySupplier ? 'primary' : 'outline-primary'"
              :disabled="fetching || loadingSuppliers"
              :busy="fetching && option.value === selectedDeliverySupplier"
              @click="selectAndQuote(option.value)"
            />
          </div>

          <b-alert
            variant="warning"
            class="mb-1 mt-1"
            :show="!loadingSuppliers && !deliverySupplierOptions.length"
          >
            {{ $t('Nenhum parceiro de entrega cadastrado.') }}
          </b-alert>

          <b-alert
            variant="danger"
            class="mb-1 mt-1"
            :show="!!validationMessage"
          >
            {{ validationMessage }}
          </b-alert>

          <b-alert
            variant="warning"
            class="mt-1 mb-0"
            :show="!!cotacaoErrorMessage"
          >
            {{ cotacaoErrorMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="order && estimate"
        class="mt-2"
      >
        <b-col>
          <FormulateInput
            :value="`#${localOrder.id} - ${localOrder.customer.name}`"
            type="label"
            :label="$t('Pedido')"
          />
          <FormulateInput
            :value="estimate.supplier && estimate.supplier.name ? estimate.supplier.name : (selectedSupplierLabel || '-')"
            type="label"
            :label="$t('Serviço de entrega')"
          />
          <FormulateInput
            :value="`${store.address.publicPlace}, ${store.address.number} - ${store.address.neighborhood}`"
            type="label"
            :label="$t('Local de retirada')"
          />
          <FormulateInput
            :value="`${localOrder.deliveryAddress.publicPlace}, ${localOrder.deliveryAddress.number} - ${localOrder.deliveryAddress.neighborhood}`"
            type="label"
            :label="$t('Local de entrega')"
          />
          <FormulateInput
            :value="`${estimate.deliveryDistance / 1000} km / ${estimate.deliveryDuration} min`"
            type="label"
            :label="$t('Distância / Tempo de entrega')"
          />
          <FormulateInput
            :value="estimate.fee / 100"
            type="label"
            :label="$t('Valor estimado')"
            :currency="$t('R$')"
            filter="currency"
            currency-symbol-position="prefix"
            :precision="2"
          />
          <FormulateInput
            :value="new Date(estimate.expiresTime * 1000)"
            type="label"
            :label="$t('Expira em')"
            filter="datetime"
          />
        </b-col>
      </b-row>
    </template>
  </e-sidebar>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { BCol, BRow, BAlert } from 'bootstrap-vue'
import ESidebar from '@/views/components/ESidebar.vue'
import EButton from '@/views/components/EButton.vue'
import { deliveryDomain } from '@/mixins'
import { getApiErrorData, getApiErrorDataMessage } from '@/utils/error-utils'

export default {
  components: { ESidebar, EButton, BCol, BRow, BAlert },

  mixins: [deliveryDomain],

  props: {
    order: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      estimate: null,
      localOrder: null,
      selectedDeliverySupplier: null,
      validationMessage: null,
      cotacaoErrorMessage: null,
      cotacaoTimeoutMs: 30000,
      loadingSuppliers: false,
    }
  },

  computed: {
    ...mapState('app', ['stores']),
    ...mapState('pages/sale/deliveryDashboard', ['filter', 'deliverySuppliers']),
    ...mapState('auth', ['userData']),
    store() {
      return this.stores.find(
        // eslint-disable-next-line eqeqeq
        store => store.id == (this.localOrder?.store?.id || this.filter.storeId)
      )
    },
    deliverySupplierOptions() {
      return (this.deliverySuppliers || [])
        .filter(supplier => !!supplier?.supplier)
        .map(supplier => ({
          value: supplier.supplier,
          label: supplier.name,
        }))
    },
    selectedSupplierLabel() {
      const match = this.deliverySupplierOptions.find(
        option => option.value === this.selectedDeliverySupplier
      )
      return match?.label || ''
    },
  },

  watch: {
    order: {
      handler(newVal) {
        if (Object.keys(newVal).length) {
          this.localOrder = { ...newVal }
        }
      },
      deep: true,
      immediate: true,
    },
    selectedDeliverySupplier() {
      this.validationMessage = null
      if (this.estimate) {
        this.estimate = null
      }
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/sale/deliveryDashboard', ['createDeliveryOrder', 'fetchDeliverySuppliers']),
    ...mapActions('pages/sale/order', ['fetchSaleById']),

    extractApiError(error) {
      const friendlyByCode = {
        address_undeliverable: this.$t('DELIVERY_ADDRESS_UNDELIVERABLE'),
      }

      try {
        const responseData = error?.response?.data
        if (responseData && typeof responseData === 'object') {
          const keys = Object.keys(responseData)
          const firstValue = keys.length ? responseData[keys[0]]?.[0] : null
          if (typeof firstValue === 'string') {
            try {
              const parsed = JSON.parse(firstValue)
              if (parsed?.code && friendlyByCode[parsed.code]) {
                return friendlyByCode[parsed.code]
              }
            } catch (e) {
              // not JSON, fall through
            }
          }
        }
      } catch (e) {
        // fall through
      }

      try {
        const errorData = getApiErrorData(error)
        if (errorData?.message) {
          return getApiErrorDataMessage(errorData)
        }
      } catch (e) {
        // fall through to fallback
      }
      return (
        error?.response?.data?.message
        || error?.message
        || this.$t('Erro desconhecido')
      )
    },

    async requestEstimate() {
      if (!this.selectedDeliverySupplier) {
        this.validationMessage = this.$t('Selecione um parceiro de entrega para continuar')
        return
      }

      if (this.fetching) {
        return
      }

      this.cotacaoErrorMessage = null
      this.fetching = true

      let timeoutHandle = null
      try {
        const estimatePromise = this.mxAskDeliveryService(
          this.localOrder,
          this.selectedDeliverySupplier
        )

        const timeoutPromise = new Promise((_, reject) => {
          timeoutHandle = setTimeout(() => {
            reject(new Error(this.$t('Tempo limite da cotação excedido')))
          }, this.cotacaoTimeoutMs)
        })

        const result = await Promise.race([estimatePromise, timeoutPromise])
        this.estimate = result
      } catch (error) {
        this.cotacaoErrorMessage = this.extractApiError(error)
      } finally {
        if (timeoutHandle !== null) {
          clearTimeout(timeoutHandle)
        }
        this.fetching = false
      }
    },
    async show(orderId) {
      this.showSidebar = true
      try {
        this.fetching = true
        this.loadingSuppliers = true
        const storeId = this.localOrder?.store?.id || this.filter?.storeId
        const tasks = [
          this.fetchDeliverySuppliers({
            storeId: storeId ? Number(storeId) : undefined,
            active: true,
          }),
        ]
        if (orderId) {
          tasks.push(
            this.fetchSaleById(orderId).then(result => {
              this.localOrder = result
            })
          )
        }
        await Promise.all(tasks)
      } catch (error) {
        this.showError({ error })
        this.hide()
      } finally {
        this.fetching = false
        this.loadingSuppliers = false
      }
      if (this.deliverySupplierOptions.length === 1) {
        this.selectAndQuote(this.deliverySupplierOptions[0].value)
      }
    },

    selectSupplier(supplier) {
      if (this.fetching || this.loadingSuppliers) return
      this.selectedDeliverySupplier = supplier
    },

    async selectAndQuote(supplier) {
      if (this.fetching || this.loadingSuppliers) return
      const optionLabel = this.deliverySupplierOptions.find(o => o.value === supplier)?.label || supplier
      const confirmed = await this.confirm({
        text: `${this.$t('Deseja realizar a cotação com')} ${optionLabel}?`,
      })
      if (!confirmed) return
      this.selectedDeliverySupplier = supplier
      await this.$nextTick()
      this.requestEstimate()
    },

    hide() {
      this.showSidebar = false
      this.selectedDeliverySupplier = null
      this.validationMessage = null
      this.cotacaoErrorMessage = null
      this.estimate = null
    },

    async localCreateDeliveryOrder() {
      try {
        this.saving = true
        await this.createDeliveryOrder({
          orderId: this.localOrder.id,
          estimateId: this.estimate.estimateId,
          deliverySupplier: this.selectedDeliverySupplier,
        })
        this.$emit('delivery-order-created')
        this.hide()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.text-bold {
  font-weight: 800;
}
</style>
